import React from "react"
import styled from "styled-components"
import { Link } from "gatsby"

import { hex2rgba } from "../../styles/tools"
import { useTranslateValue } from "../../data/TranslateContext"

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
`

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: 4rem;
`

const NavLink = styled(Link)`
  display: block;
  padding: 0.5rem 1.5rem;

  color: ${props => props.theme.color.lightBg};
  font-family: ${props => props.theme.font.heading};
  font-size: 2.4em;

  &[aria-current="page"] {
    color: ${props => props.theme.color.secondary};
  }

  &:not(:last-child) {
    margin-bottom: 2.5rem;
  }

  @media (max-width: 768px) {
    font-size: 1.8em;
  }
`

const ToggleNav = styled.nav`
  position: absolute;
  left: 50%;
  bottom: 12vh;
  transform: translateX(-50%);

  display: flex;
  padding: 0.5rem 0;

  a {
    display: block;
    padding: 0.2rem 0.5rem;
    color: ${props => props.theme.color.navigationBg};
    transition: color 0.2s ease-in-out;

    font-family: ${props => props.theme.font.heading};
    font-size: 1.2em;
  }

  [data-button="toggle"] {
    position: relative;

    display: block;
    margin: 0 0.5rem;
    padding: 0.25rem;

    span {
      position: absolute;
      left: 0;
      top: 0;

      display: block;
      width: 0px;
      height: 0px;
      overflow: hidden;
      font-size: 0;
      color: transparent;
      user-select: none;
    }

    &::before,
    &::after {
      position: relative;
      display: block;
      content: "";
      transition: all 0.2s ease-in-out;
    }
    &::before {
      width: 3.75rem;
      height: 100%;

      border-radius: 2rem;
      background-color: ${props => props.theme.color.navigationBg};
      box-shadow: 0 1px 3px
        ${props => {
          return hex2rgba(props.theme.color.primaryBackground, 0.5)
        }}
        inset;
    }
    &::after {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);

      width: 2rem;
      height: 2rem;

      border-radius: 2rem;
      box-shadow: 0 2px 4px
        ${props => {
          return hex2rgba(props.theme.color.primaryBackground, 0.6)
        }};
      background-color: ${props => props.theme.color.highlight};
    }
  }
`

const LanguageToggle = styled.div`
  position: absolute;
  left: 50%;
  bottom: 22vh;
  transform: translateX(-50%);

  display: flex;
  padding: 0.5rem 0;

  button {
    position: relative;

    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 0.5rem;
    padding: 0;

    background: none;
    border: none;
    cursor: pointer;

    color: ${props => props.theme.color.navigationBg};
    font-family: ${props => props.theme.font.heading};
    font-size: 1rem;
    line-height: 1rem;
    transition: all 0.1s ease-in-out;

    &:hover {
      color: #fff;
    }

    &::before {
      position: absolute;
      top: calc(100% + 2px);
      left: 50%;
      transform: translateX(-50%) scaleX(0);
      transform-origin: center;

      width: 100%;
      height: 2px;
      background-color: #fff;
      transition: all 0.1s ease-in-out;

      content: "";
    }

    &[data-active="true"]::before {
      transform: translateX(-50%) scaleX(0.8);
    }

    &:not(:last-child)::after {
      position: absolute;
      top: 50%;
      left: 100%;
      transform: translateX(0.375rem) translateY(-50%);

      display: block;
      margin: 0;
      padding: 0;
      color: #fff;
      opacity: 0.5;

      content: "/";
    }
  }
`

export default function TouchModal(props) {
  const { setActive } = props

  const [{ language }, dispatch] = useTranslateValue()

  return (
    <Content>
      <Nav>
        <NavLink
          to="/flavors"
          onClick={e => {
            setActive(false)
          }}
        >
          {language === "en" && "The Flavors"}
          {language === "fr" && "Les saveurs"}
        </NavLink>
        <NavLink
          to="/how-it-works"
          onClick={e => {
            setActive(false)
          }}
        >
          {language === "en" && "How It Works"}
          {language === "fr" && "Comment ça Fonctionne"}
        </NavLink>
        <NavLink
          to="/our-story"
          onClick={e => {
            setActive(false)
          }}
        >
          {language === "en" && "Our Story"}
          {language === "fr" && "Notre Histoire"}
        </NavLink>
        <NavLink
          to="/finder"
          onClick={e => {
            setActive(false)
          }}
        >
          {language === "en" && "Find a f'real"}
          {language === "fr" && "Trouvez un f'real"}
        </NavLink>
      </Nav>

      <LanguageToggle>
        <button
          data-active={language === "en"}
          onClick={e => {
            dispatch({ type: "UPDATE_LANGUAGE", payload: "en" })
          }}
        >
          English
        </button>
        <button
          data-active={language === "fr"}
          onClick={e => {
            dispatch({ type: "UPDATE_LANGUAGE", payload: "fr" })
          }}
        >
          Français
        </button>
      </LanguageToggle>

      <ToggleNav>
        <a data-button="business" href="/business">
          {language === "en" && "Business"}
          {language === "fr" && "Entreprise"}
        </a>
        <a data-button="toggle" href="/business">
          <span>Go to Business Site</span>
        </a>
        <Link data-button="consumer" to="/">
          {language === "en" && "Consumer"}
          {language === "fr" && "Consommateur"}
        </Link>
      </ToggleNav>
    </Content>
  )
}
